import React, { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import axios from '../../utils/axios';
import { AxiosResponse } from 'axios';
import { API_URL } from '../../constants';
import { StripedDataGrid } from '../../styledComponents/datagrid';
import { renderCellExpand } from '../../components/library/DataGrid/renderCellExpand';
import {
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid';
import {
  Alert,
  Box,
  CircularProgress,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import { PageWrapper } from '../../styledComponents/wrappers';
import { FormatDateString } from '../../utils/helper';
import {
  FulfillmentLifecycleStatus,
  OrderType,
  FulfillmentOrderSummary,
} from '../../services/fulfillment/types';
import {
  FULFILLMENT_PAGE_SIZE,
  LIFECYCLE_STATUSES,
  ORDER_TYPE_LABELS,
} from '../../services/fulfillment/constants';
import FulfillmentStatusChip from './FulfillmentStatusChip';

export const FULFILLMENT_ORDERS_ROUTE = '/fulfillment/orders';

type StatusFilter = 'all' | 'active' | 'ready_to_pack' | 'exceptions' | 'shipped';

const columns: GridColDef[] = [
  {
    headerName: 'Order #',
    field: 'shopifyOrderName',
    flex: 0.7,
    renderCell: renderCellExpand,
  },
  {
    headerName: 'Customer',
    field: 'customerName',
    flex: 1,
    renderCell: renderCellExpand,
  },
  {
    headerName: 'Status',
    field: 'lifecycleStatus',
    flex: 1.1,
    sortable: true,
    sortComparator: (
      a: FulfillmentLifecycleStatus | null,
      b: FulfillmentLifecycleStatus | null,
    ) => {
      return (
        LIFECYCLE_STATUSES.indexOf(a ?? 'pending') - LIFECYCLE_STATUSES.indexOf(b ?? 'pending')
      );
    },
    renderCell: (params: GridRenderCellParams<FulfillmentOrderSummary>) => (
      <FulfillmentStatusChip
        lifecycleStatus={params.row.lifecycleStatus}
        waveStatus={params.row.waveStatus}
        waveId={params.row.waveId}
      />
    ),
  },
  {
    headerName: 'Items',
    field: 'lineItemCount',
    flex: 0.4,
    type: 'number',
  },
  {
    headerName: 'Order type',
    field: 'orderType',
    flex: 0.8,
    valueFormatter: ({ value }) => ORDER_TYPE_LABELS[value as OrderType] ?? value,
  },
  {
    headerName: 'Order date',
    field: 'createdAt',
    flex: 0.8,
    valueFormatter: ({ value }) => (value ? FormatDateString(value) : ''),
  },
];

const FulfillmentOrdersList: React.FC = () => {
  const history = useHistory();
  const [orders, setOrders] = useState<FulfillmentOrderSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: FULFILLMENT_PAGE_SIZE,
  });

  useEffect(() => {
    const fetchOrders = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_URL}/fulfillment/orders`);
        setOrders((response as AxiosResponse<FulfillmentOrderSummary[]>).data);
      } catch {
        setError('Failed to load fulfillment orders.');
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, []);

  const filteredOrders = useMemo(() => {
    let result = orders;

    // Apply status filter first
    if (statusFilter === 'active') {
      result = result.filter(o =>
        ['picking', 'picked', 'packing', 'packed'].includes(o.lifecycleStatus ?? ''),
      );
    } else if (statusFilter === 'ready_to_pack') {
      result = result.filter(o => o.lifecycleStatus === 'picked');
    } else if (statusFilter === 'exceptions') {
      result = result.filter(o => o.waveStatus === 'exception');
    } else if (statusFilter === 'shipped') {
      result = result.filter(o => o.lifecycleStatus === 'shipped');
    }

    // Then apply search term
    if (!searchTerm.trim()) return result;
    const term = searchTerm.toLowerCase();
    return result.filter(
      o =>
        o.shopifyOrderName.toLowerCase().includes(term) ||
        o.customerName.toLowerCase().includes(term),
    );
  }, [orders, searchTerm, statusFilter]);

  const handleRowClick = (params: GridRowParams) => {
    history.push(`/fulfillment/orders/${(params.row as FulfillmentOrderSummary).shopifyOrderId}`);
  };

  const handleStatusFilterChange = (_: React.SyntheticEvent, value: StatusFilter) => {
    setStatusFilter(value);
    setPaginationModel(m => ({ ...m, page: 0 }));
  };

  return (
    <PageWrapper>
      <Typography variant="h5" gutterBottom>
        Fulfillment Orders
      </Typography>
      <Tabs
        value={statusFilter}
        onChange={handleStatusFilterChange}
        textColor="primary"
        indicatorColor="primary"
        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="All" value="all" />
        <Tab label="Active" value="active" />
        <Tab label="Ready to Pack" value="ready_to_pack" />
        <Tab label="Exceptions" value="exceptions" />
        <Tab label="Shipped" value="shipped" />
      </Tabs>
      <Box mb={2}>
        <TextField
          size="small"
          placeholder="Search by order # or customer"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          style={{ width: 320 }}
        />
      </Box>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }}>
          {error}
        </Alert>
      )}
      {loading ? (
        <Box display="flex" justifyContent="center" mt={4}>
          <CircularProgress />
        </Box>
      ) : (
        <StripedDataGrid
          rows={filteredOrders}
          columns={columns}
          getRowId={row => row.shopifyOrderId}
          onRowClick={handleRowClick}
          autoHeight
          disableRowSelectionOnClick
          paginationModel={paginationModel}
          onPaginationModelChange={model => setPaginationModel(model)}
          pageSizeOptions={[
            FULFILLMENT_PAGE_SIZE,
            FULFILLMENT_PAGE_SIZE * 2,
            FULFILLMENT_PAGE_SIZE * 4,
          ]}
          initialState={{
            sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
            pagination: { paginationModel: { pageSize: FULFILLMENT_PAGE_SIZE } },
          }}
          sx={{ cursor: 'pointer' }}
          rowHeight={56}
        />
      )}
    </PageWrapper>
  );
};

export default FulfillmentOrdersList;
